import { joiResolver } from '@hookform/resolvers/joi';
import { Box, Grid, Link, Paper, Typography } from '@mui/material';
import Joi from 'joi';
import { observer } from 'mobx-react-lite';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ReCAPTCHA from 'react-google-recaptcha';
import settings from '../../../global/config/settings';
import { API } from '../../../global/config/api';
import { XsButton, XsTextField } from '../../../global/ui';
import { useServerCall } from '../../../global/helpers/hooks';
import { isEmailValid } from '../../../global/helpers/validators';
import GlobalStore from '../../../global/stores/GlobalStore';
import UIStore from '../../../global/stores/UIStore';
import LoginStore from './LoginStore';

interface IForgotPasswordForm {
  email: string;
}

function ForgotPassword() {
  const { t } = useTranslation();

  const schema = Joi.object<IForgotPasswordForm>({
    email: Joi.string()
      .custom((v, h) => (isEmailValid(v) ? v : h.error('any.invalid')))
      .required()
  });

  const form = useForm<IForgotPasswordForm>({ resolver: joiResolver(schema) });
  const { control, handleSubmit } = form;

  const resetCall = useServerCall<{ email: string }, any>({
    key: 'resetPassword',
    mode: 'control',
    request: { method: 'POST' },
    queryOptions: { refetchOnWindowFocus: false },
    onSuccess: () => {
      GlobalStore.loading(false);
      UIStore.setNotificationMessage(t('login.reset_success'), 'success');
      LoginStore.setScreen('login');
    },
    onError: () => {
      GlobalStore.loading(false);
    }
  });

  const onSubmit = handleSubmit(async (data) => {
    GlobalStore.loading(true);
    const token = await LoginStore.getRecaptchaToken('client_reset_password');
    resetCall.run({ url: API.resetPassword(token), data: { email: data.email } });
  });

  return (
    <Grid container direction='column' spacing={3}>
      <Grid item className='textCenter'>
        <Typography variant='h3'>{t('login.reset_title')}</Typography>
      </Grid>
      <Grid item>
        <Paper variant='elevation' elevation={2} style={{ backgroundColor: 'transparent' }}>
          <Box p={2}>
            <form onSubmit={onSubmit}>
              <FormProvider {...form}>
                <Grid item container direction='column' spacing={3}>
                  <Grid item container direction='column' spacing={1}>
                    <Grid item>
                      <Typography variant='subtitle2'>{t('login.reset_subtitle')}</Typography>
                    </Grid>
                    <Grid item>
                      <Typography variant='body2'>{t('login.reset_step1')}</Typography>
                    </Grid>
                    <Grid item>
                      <Typography variant='body2'>{t('login.reset_step2')}</Typography>
                    </Grid>
                    <Grid item>
                      <Typography variant='body2'>{t('login.reset_step3')}</Typography>
                    </Grid>
                  </Grid>
                  <Grid item>
                    <Controller
                      name='email'
                      control={control}
                      defaultValue=''
                      render={({ field }) => <XsTextField {...field} autoFocus label={t('login.reset_email_label')} />}
                    />
                  </Grid>
                  <Grid item>
                    <XsButton type='submit' variant='contained' label='login.reset_submit' />
                  </Grid>
                  {GlobalStore.enabledRecaptchaV2 && (
                    <Grid item>
                      <ReCAPTCHA
                        sitekey={settings.SITE_KEY_V2 ?? ''}
                        onChange={(tok) => (GlobalStore.tokenRecaptchaV2 = tok ?? undefined)}
                      />
                    </Grid>
                  )}
                  <Grid item>
                    <Link variant='body2' className='pointer' onClick={() => LoginStore.setScreen('login')}>
                      {t('login.back_to_login')}
                    </Link>
                  </Grid>
                </Grid>
              </FormProvider>
            </form>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
}

export default observer(ForgotPassword);
